import { DatePicker, DatePickerProps } from '@pluxee-design-system/datepicker';
import { SkippableBaseControlProps } from '@pluxee-design-system/react-hook-form';
import useConfig from 'data/useConfig';
import useCurrentLocale from 'data/useCurrentLocale';
import { format, formatISO, set } from 'date-fns';
import { useCallback, useMemo } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { useController } from 'react-hook-form';
import { getDateLocale } from 'utils/dateLocale';

interface DateControlProps
  extends Omit<DatePickerProps, 'name' | 'onChange' | 'showTimeInput' | 'value' | 'variant'>,
    SkippableBaseControlProps {
  filterDate?: (date: Date) => boolean; // TODO: not supported?
  withEndTime?: boolean;
  withTime?: boolean;
}

const DATE_FORMAT = 'yyyy-MM-dd';
const isMidnight = (date: Date) => date.getHours() === 0 && date.getMinutes() === 0;

const DateControl = ({
  control,
  isReadOnly,
  name,
  placeholder,
  readonly,
  rules,
  skipVariant = false,
  withEndTime = false,
  withTime,
  ...datePickerProps
}: DateControlProps) => {
  const { field, fieldState } = useController({
    name,
    control,
    rules,
  });
  const { error } = fieldState;
  const { onChange } = field;
  const { dateFormat } = useConfig();
  const { value: currentLocale } = useCurrentLocale();
  const locale = getDateLocale(currentLocale).code;
  const hasError = Boolean(error);

  const defaultPlaceholder = useMemo(
    () => dateFormat.date.replace(/([a-zA-Z])/g, '-'),
    [dateFormat.date],
  );
  const handleChange = useCallback(
    (date: Date | null) => {
      let result: string | null = null;
      if (date) {
        if (withTime) {
          result = formatISO(
            withEndTime && isMidnight(date)
              ? set(date, { hours: 23, minutes: 59, seconds: 0, milliseconds: 0 })
              : set(date, { seconds: 0, milliseconds: 0 }),
          );
        } else {
          result = format(date, DATE_FORMAT);
        }
      }
      onChange(result);
    },
    [withEndTime, onChange, withTime],
  );

  return (
    <DatePicker
      // @ts-expect-error Passed by ...rest
      name={field.name}
      locale={locale}
      onBlur={field.onBlur}
      onChange={handleChange}
      value={(field.value && new Date(field.value)) || null}
      placeholder={placeholder ?? defaultPlaceholder}
      {...datePickerProps}
      variant={
        skipVariant
          ? fieldState.isTouched && hasError
            ? 'error'
            : undefined
          : readonly ?? isReadOnly
            ? 'readonly'
            : fieldState.isTouched && hasError
              ? 'error'
              : undefined
      }
      helpText={error?.message ?? datePickerProps.helpText}
      format={withTime ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy'}
      showTimeInput={withTime}
    />
  );
};

export default DateControl;
